import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { format, subDays, startOfMonth, startOfYear } from 'date-fns';
import { Calendar } from 'lucide-react';
import { useState } from 'react';

interface DateRangePickerProps {
  dateFrom: string | null;
  dateTo: string | null;
  onChange: (from: string | null, to: string | null) => void;
}

const presets = [
  { label: "Aujourd'hui", from: () => new Date(), to: () => new Date() },
  { label: '7 jours', from: () => subDays(new Date(), 7), to: () => new Date() },
  { label: '30 jours', from: () => subDays(new Date(), 30), to: () => new Date() },
  { label: 'Ce mois', from: () => startOfMonth(new Date()), to: () => new Date() },
  { label: "Cette année", from: () => startOfYear(new Date()), to: () => new Date() },
];

export function DateRangePicker({ dateFrom, dateTo, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const activeLabel = dateFrom && dateTo
    ? `${format(new Date(dateFrom), 'dd/MM')} - ${format(new Date(dateTo), 'dd/MM')}`
    : 'Période';

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        icon={<Calendar className="h-3.5 w-3.5" />}
        onClick={() => setOpen(!open)}
      >
        {activeLabel}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-neutral-200 bg-white p-3 shadow-hard dark:border-neutral-700 dark:bg-neutral-800">
            <div className="space-y-1">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 text-left text-sm text-neutral-700 transition-all duration-200 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700',
                    dateFrom === format(preset.from(), 'yyyy-MM-dd') && 'bg-primary-500/20 text-primary-600 dark:text-primary-400'
                  )}
                  onClick={() => {
                    onChange(format(preset.from(), 'yyyy-MM-dd'), format(preset.to(), 'yyyy-MM-dd'));
                    setOpen(false);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="mt-2 border-t border-neutral-200 pt-2 dark:border-neutral-700">
              <div className="flex gap-2">
                <input
                  type="date"
                  value={dateFrom || ''}
                  onChange={(e) => onChange(e.target.value, dateTo)}
                  className="h-8 w-full rounded-lg border border-neutral-300 bg-neutral-100 px-2 text-xs text-neutral-800 transition-all duration-200 focus:border-primary-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-200"
                />
                <input
                  type="date"
                  value={dateTo || ''}
                  onChange={(e) => onChange(dateFrom, e.target.value)}
                  className="h-8 w-full rounded-lg border border-neutral-300 bg-neutral-100 px-2 text-xs text-neutral-800 transition-all duration-200 focus:border-primary-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-200"
                />
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { onChange(null, null); setOpen(false); }}
                className="mt-2 w-full text-center text-xs text-neutral-500 transition-all duration-200 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                Effacer
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
